import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Headline, Text, TextButton } from '@oneaudi/unified-web-alpha-components';
import useLockBodyScroll from '../../app/hooks/useLockBodyScroll';
import { parseDigitalLeadsData } from '../../app/utils/digitalLeadsFormat';
import { selectZipCode } from '../../app/reducers/zipCodeReducer';
import { priceFormat } from '../../app/utils/formatPrice';
import formatModelName from '../../app/utils/formatModelName';
import removeYearFromModelName from '../../app/utils/removeYearFromModelName';
import PaymentsOptions from '../../app/components/VehicleOffersList/VehicleTile/PricingOptions/PaymentsOptions';
import PaymentOptionsCa from '../../app/components/VehicleOffersList/VehicleTileCa/PaymentOptionsCa';
import type { ModalProps } from './Modal.legacy';
import {
  StyledDialog,
  TitleWrapper,
  MSRP,
  MainTextWrapper,
  PaymentsWrapper,
  CTAsContainer,
  ExpirationDateWrapper,
} from './Modal.alpha.styles';

const ModalAlpha: React.FC<ModalProps> = ({
  offer,
  onCloseModal,
  inventoryUrl,
  dealerId,
  isStackableOffers,
  isModal,
}) => {
  useLockBodyScroll(true);

  const { t, i18n } = useTranslation();

  const isFrenchPage = i18n.language === 'fr';
  const {
    offerModelDetail,
    offerDisclaimer,
    offerDisclaimerFr,
    stackableOffers,
    programName,
    creditBonus,
    offerType,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    term,
    aprRate,
  } = offer;

  const { modelCodeDescription, modelYear, trimline, vehicleType, modelName } = offerModelDetail;

  const stackOffer = stackableOffers[0];
  const isBonusOffers = offerType === 'credit' || isStackableOffers;

  const isCpoVehicle = vehicleType === 'USED-CAR';

  const { confirmedZipcode, market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const msrp = priceFormat(offer?.offerModelDetail?.msrp);
  const trimmedModelName =
    offer && isCaMarket ? modelName : removeYearFromModelName(modelYear, modelCodeDescription);
  const modelFullName = isCaMarket ? `${modelName}` : formatModelName(modelCodeDescription);

  const digitalLeadsInfo = useMemo(() => {
    return parseDigitalLeadsData(offer, isCaMarket, confirmedZipcode, dealerId);
  }, [offer, confirmedZipcode, dealerId, isCaMarket]);

  const programNameCopy = stackOffer?.programName ?? programName;

  const offerLabel = useMemo(() => {
    if (programNameCopy === 'AUD_EV_CREDIT1') return 'EV Lease Bonus';
    if (programNameCopy === 'AUD_SOA_COMBINED') return 'Season of Audi Bonus + Audi Credit';
    if (programNameCopy === 'AUD_SALES_EVENT') return 'Season of Audi Customer Bonus';
    if (programNameCopy === 'AUD_CUSTOMER_CREDIT' || !!stackOffer) return 'Customer Bonus';
    if (programNameCopy === 'AUD_COSTCO_SELECT_PROGRAM') return 'Costco Select Bonus';
    if (programNameCopy === 'AUD_CONQUEST') return 'Conquest Bonus';
    if (programNameCopy === 'AUD_LOYALTY') return 'Loyalty Bonus';
    return 'Audi Credit';
  }, [programNameCopy, stackOffer]);

  const disclaimer = isFrenchPage && offerDisclaimerFr ? offerDisclaimerFr : offerDisclaimer;

  const creditOfferTitle = useMemo(() => {
    const bonusValue = stackOffer ? priceFormat(stackOffer?.creditBonus) : priceFormat(creditBonus);
    return `${bonusValue} ${offerLabel}`;
  }, [creditBonus, offerLabel, stackOffer]);

  const renderLeaseDetails = useMemo(() => {
    return (
      <div>
        <div>
          <Text data-testid="offer-details-tag" variant={{ order: '2', style: 'bold' }}>
            {t(`promo.offers.details.${offerType}`)}
          </Text>
        </div>
        <TitleWrapper>
          <Headline
            tag="h4"
            variant={{
              order: '4',
              type: 'headline',
              weight: 'normal',
            }}
          >
            {t('promo.offers.details.offerDetails')}
          </Headline>
          <Headline
            data-testid="offer-details-name"
            tag="h2"
            variant={{
              order: '2',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {modelFullName}
          </Headline>
        </TitleWrapper>
        {isCaMarket && !isCpoVehicle && (
          <MSRP>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('promo.tile.msrp')}
            </Text>
            <Headline
              tag="h4"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'bold',
              }}
            >
              {msrp}
            </Headline>
          </MSRP>
        )}

        <PaymentsWrapper>
          {isCaMarket ? (
            <PaymentOptionsCa offer={offer} isModal={isModal} />
          ) : (
            <PaymentsOptions offer={offer} />
          )}
        </PaymentsWrapper>
      </div>
    );
  }, [isCpoVehicle, isModal, modelFullName, msrp, offer, offerType, t, isCaMarket]);

  const renderCreditDetails = useMemo(() => {
    return (
      <div>
        <div>
          <Text data-testid="bonus-details-tag" variant={{ order: '2', style: 'bold' }}>
            {t('promo.offers.details.creditTag')}
          </Text>
        </div>
        <TitleWrapper>
          <Headline
            data-testid="bonus-details-title"
            tag="h3"
            variant={{
              order: '3',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {creditOfferTitle}
          </Headline>
        </TitleWrapper>
      </div>
    );
  }, [creditOfferTitle, t]);

  return (
    <StyledDialog
      open
      variant="focus-large"
      onClose={onCloseModal}
      onCloseButtonAriaLabel={t('promo.modal.close')}
      aria-label={t('promo.modal.label')}
      data-testid="modal-container"
      data-modal-title={t('promo.modal.label')}
      data-promotion-type={offerType}
      data-promotion-offerdate={programToDate}
      data-promotion-credit={isBonusOffers ? (stackOffer?.creditBonus ?? creditBonus) : creditBonus}
      data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'}
      data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'}
      data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'}
      data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'}
      data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'}
      data-vehicle-model-name={trimmedModelName}
      data-vehicle-model-year={modelYear}
      data-vehicle-price={msrp !== '-' ? msrp : 'null'}
      data-vehicle-trim={trimline}
    >
      {isBonusOffers ? renderCreditDetails : renderLeaseDetails}
      <MainTextWrapper>
        <Text
          data-testid={isBonusOffers ? 'bonus-details-description' : 'offer-details-description'}
          variant={{ order: '1', style: 'normal' }}
        >
          {isStackableOffers && stackOffer?.offerDisclaimer
            ? stackOffer.offerDisclaimer
            : disclaimer}
        </Text>
      </MainTextWrapper>
      <ExpirationDateWrapper>
        <Text
          data-testid={
            isBonusOffers ? 'bonus-details-expiration-date' : 'offer-details-expiration-date'
          }
          variant={{ order: '2', style: 'normal' }}
        >
          {t('promo.offers.details.validationUntil', {
            date: `${offer.programToDate}`,
          })}
        </Text>
      </ExpirationDateWrapper>
      <CTAsContainer>
        <TextButton
          href={inventoryUrl}
          data-testid="offer-details-inventory-cta"
          target="_blank"
          variant="primary"
        >
          {t('promo.offers.cta.inventory')}
        </TextButton>
        <TextButton
          id="digital-leads"
          data-info={digitalLeadsInfo}
          data-testid="offer-details-contact-cta"
          onClick={onCloseModal}
          variant="secondary"
        >
          {t('promo.offers.cta.contact')}
        </TextButton>
      </CTAsContainer>
    </StyledDialog>
  );
};

export default ModalAlpha;
